import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY } from "@/lib/siteData";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Work", href: "#gallery" },
  { label: "Why Us", href: "#manifesto" },
  { label: "Reviews", href: "#reviews" },
  { label: "Areas", href: "#areas" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-9 md:top-10 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-300 ${
        scrolled ? "bg-white/70 backdrop-blur-xl border-b border-black/5" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="h-[2px] w-full brand-gradient" />
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-16 md:h-20 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("#top")} className="flex items-center gap-3 group">
          <img src={COMPANY.icon} alt="Aqua Control Plumbing" className="h-12 w-auto md:h-14 object-contain" />
          <span className="font-display font-extrabold text-lg md:text-2xl tracking-tight leading-none text-left text-[#007AFF]">
            Aqua Control
            <span className="block text-[11px] md:text-sm font-semibold tracking-[0.22em] text-[#FF3B30] uppercase mt-1">Plumbing & Hydronics</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              onClick={() => go(l.href)}
              className="text-sm font-medium text-neutral-600 hover:text-black transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:brand-gradient hover:after:w-full after:transition-[width] after:duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-call"
            href={COMPANY.phoneHref}
            className="hidden sm:flex items-center gap-2 text-sm font-semibold text-neutral-800 hover:text-[#007AFF] transition-colors"
          >
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={() => go("#quote")}
            className="hidden sm:inline-flex items-center rounded-full bg-black text-white text-sm font-semibold px-5 py-2.5 hover:bg-[#007AFF] transition-colors duration-300"
          >
            Get a Quote
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden p-2 -mr-2">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-black/5"
        >
          <div className="px-5 py-5 flex flex-col gap-4">
            {LINKS.map((l) => (
              <button key={l.href} onClick={() => go(l.href)} className="text-left text-base font-medium text-neutral-700">
                {l.label}
              </button>
            ))}
            <a href={COMPANY.phoneHref} className="flex items-center gap-2 font-semibold text-[#007AFF]">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <button onClick={() => go("#quote")} className="rounded-full bg-black text-white font-semibold px-5 py-3">
              Get a Quote
            </button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
